import { createMachine, assign, send, DoneInvokeEvent } from "xstate";

// The adapter dispatches; the slice's action creator on its own never reaches
// the store, which is why fetch failures used to fail silently.
import { createNotification } from "@/game/engine/notification";
import { Severity } from "@/stores/notifications";
import { MediaLink, MediaType } from "@/types/Media";

const PRELOAD_LINK_THRESHOLD = 5;
/** How long to wait before retrying a fetch that failed or came back empty. */
const FETCH_RETRY_DELAY = 5_000;

function preloadImage(url: string) {
  new Image().src = url;
}

export type MediaMachine = ReturnType<typeof createMediaMachine>;

export type MediaMachineContext = {
  links: MediaLink[];
  linkIndex: number;
  /**
   * Set when a slide change arrives while there's no link to advance to.  The
   * slideshow only asks for the next link when the current one finishes, so a
   * dropped `NEXT_LINK` is never re-sent.  Remembering it lets the advance be
   * replayed once a fetch delivers more links.
   */
  pendingAdvance: boolean;
};

export type StopEvent = {
  type: "STOP";
};

export type MediaMachineEvent =
  | { type: "FETCH" }
  | { type: "LOAD_MORE_LINKS" }
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "NEXT_LINK" }
  | { type: "PREVIOUS_LINK" }
  | { type: "PRELOAD_LINK" }
  | StopEvent;

type FetchedEvent = DoneInvokeEvent<MediaLink[] | undefined>;

function fetchedLinks(context: MediaMachineContext, event: FetchedEvent) {
  return context.links.concat(event.data ?? []);
}

export function createMediaMachine(
  getLinks: () => Promise<MediaLink[] | undefined>
) {
  // Only surface the first failure, otherwise every retry stacks a notification.
  let reportedFetchError = false;

  const skipLink = {
    PREVIOUS_LINK: {
      cond: "hasPreviousLink",
      actions: "previousLink",
    },
    PRELOAD_LINK: {
      cond: "isNextLinkPreloadable",
      actions: "preloadLink",
    },
  };

  const mediaMachine = createMachine<MediaMachineContext, MediaMachineEvent>(
    {
      id: "media",
      initial: "fetching",
      context: {
        links: [],
        linkIndex: 0,
        pendingAdvance: false,
      },
      on: {
        STOP: {
          target: "stopped",
        },
      },
      states: {
        stopped: {
          type: "final",
        },
        fetching: {
          invoke: {
            id: "fetchMedia",
            src: getLinks,
            onDone: [
              {
                // The fetch resolved a slide change that was waiting on links.
                cond: "canResumeAdvance",
                target: "playing",
                actions: [
                  "appendLinks",
                  "clearPendingAdvance",
                  send("NEXT_LINK"),
                ],
              },
              {
                // Still nothing to advance to, so back off and try again.
                cond: "isAdvancePending",
                target: "retrying",
                actions: "appendLinks",
              },
              {
                target: "playing",
                actions: "appendLinks",
              },
            ],
            onError: {
              target: "retrying",
              actions: "reportFetchError",
            },
          },
          on: {
            ...skipLink,
            PAUSE: "paused",
            NEXT_LINK: [
              {
                cond: "hasNextLink",
                actions: ["nextLink", send("PRELOAD_LINK")],
              },
              { actions: "queueAdvance" },
            ],
          },
        },
        /**
         * Media ran dry or the fetch failed.  Retry on a timer instead of
         * parking forever, which used to leave the game with no media at all.
         */
        retrying: {
          after: {
            [FETCH_RETRY_DELAY]: "fetching",
          },
          on: {
            ...skipLink,
            FETCH: "fetching",
            PLAY: "playing",
            PAUSE: "paused",
            NEXT_LINK: [
              {
                cond: "hasNextLink",
                actions: ["nextLink", send("PRELOAD_LINK")],
              },
              { target: "fetching", actions: "queueAdvance" },
            ],
          },
        },
        paused: {
          on: {
            FETCH: "fetching",
            PLAY: "playing",
          },
        },
        playing: {
          on: {
            ...skipLink,
            FETCH: "fetching",
            PAUSE: "paused",
            NEXT_LINK: [
              {
                cond: "hasNextLink",
                actions: [
                  "nextLink",
                  send("PRELOAD_LINK"),
                  send("LOAD_MORE_LINKS"),
                ],
              },
              // Out of links: fetch more and replay the advance when they land.
              { target: "fetching", actions: "queueAdvance" },
            ],
            LOAD_MORE_LINKS: {
              cond: "shouldLoadMoreLinks",
              target: "fetching",
            },
          },
        },
      },
    },
    {
      actions: {
        preloadLink: ({ linkIndex, links }) => {
          const nextLink = links[linkIndex + 1];
          preloadImage(nextLink.directLink);
        },
        nextLink: assign((context) => ({
          linkIndex: context.linkIndex + 1,
        })),
        previousLink: assign((context) => ({
          linkIndex: context.linkIndex - 1,
        })),
        appendLinks: assign((context, event) => ({
          links: fetchedLinks(context, event as FetchedEvent),
        })),
        queueAdvance: assign({ pendingAdvance: (_context) => true }),
        clearPendingAdvance: assign({ pendingAdvance: (_context) => false }),
        reportFetchError: (_context, event) => {
          if (reportedFetchError) {
            return;
          }
          reportedFetchError = true;

          createNotification({
            message: `Error fetching media: ${
              (event as DoneInvokeEvent<Error>).data?.message
            }`,
            duration: -1,
            severity: Severity.ERROR,
          });
        },
      },
      guards: {
        isNextLinkPreloadable: ({ links, linkIndex }) => {
          let nextLinkPreloadable = false;

          const nextLink = links[linkIndex + 1];
          if (
            nextLink &&
            (nextLink.mediaType === MediaType.Picture ||
              nextLink.mediaType === MediaType.Gif)
          ) {
            nextLinkPreloadable = true;
          }

          return nextLinkPreloadable;
        },
        shouldLoadMoreLinks: ({ links, linkIndex }) => {
          return links.length - linkIndex < PRELOAD_LINK_THRESHOLD;
        },
        hasNextLink: ({ links, linkIndex }) => {
          return Boolean(links[linkIndex + 1]);
        },
        hasPreviousLink: ({ linkIndex }) => {
          return linkIndex > 0;
        },
        isAdvancePending: ({ pendingAdvance }) => pendingAdvance,
        canResumeAdvance: (context, event) => {
          return (
            context.pendingAdvance &&
            Boolean(
              fetchedLinks(context, event as FetchedEvent)[
                context.linkIndex + 1
              ]
            )
          );
        },
      },
    }
  );

  return mediaMachine;
}
